import { Link, useLocation } from 'react-router-dom';
import { PenLine } from 'lucide-react';
import { api } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { useQuery } from '../../../lib/useQuery';

/** What `GET /signatures/mine` returns, as far as these screens read it. */
export interface MySignatureState {
  fullName: string | null;
  current: { version: number } | null;
  professional: { designation: string | null; qualification: string | null; registrationNo: string | null; registrationCouncil: string | null };
  availableForSigning: boolean;
  signingActs: string[];
}

/**
 * The signer's own record, for a login whose work puts a name on a report.
 * Null for a login without `signature.manage-own`: it has nothing to set up,
 * and asking would only earn a 403.
 */
export function useMySignature() {
  const { permissions } = useAuth();
  const can = permissions.has('signature.manage-own');
  return useQuery<MySignatureState | null>(
    () => (can ? api.get<MySignatureState>('/signatures/mine') : Promise.resolve(null)),
    [can],
  );
}

/** The console's My signature page, beside whichever lab screen this is. */
export function signaturePath(pathname: string): string {
  return pathname.replace(/\/[^/]+$/, '/signature');
}

/**
 * Said before the work, not discovered on the PDF: a report this person
 * enters, verifies or signs prints their signature image — or, with none on
 * file, "Signed electronically · no signature image on file" in its place.
 * Shown only when something is missing; a ready signer sees nothing.
 */
export function SignatureReadiness({ act }: { act: 'enter' | 'sign' }) {
  const mine = useMySignature();
  const { pathname } = useLocation();
  const s = mine.data;
  if (!s || !s.signingActs.length) return null;
  const noImage = !s.current;
  // A registration number matters on a signature that releases a report,
  // not on a technician's "performed by".
  const noRegistration = act === 'sign' && !s.professional.registrationNo;
  if (!noImage && !noRegistration) return null;
  const missing = [noImage && 'signature image', noRegistration && 'registration number'].filter(Boolean).join(' and ');
  // One line. The longer explanation lives on My signature.
  return (
    <div role="status" className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-input border border-warning-600 bg-warning-100 px-3 py-2 text-small text-warning-600">
      <PenLine aria-hidden className="h-4 w-4 shrink-0" />
      <span className="font-semibold">Complete your signing profile</span>
      {/* Recommended, not required: reports still sign without these. */}
      <span className="text-neutral-700">
        No {missing} on file — recommended; reports still sign without {noImage && noRegistration ? 'them' : 'it'}.
      </span>
      <Link to={signaturePath(pathname)} className="font-semibold underline underline-offset-2 max-md:inline-flex max-md:min-h-10 max-md:items-center">
        Open My signature
      </Link>
    </div>
  );
}
